import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, deleteDoc, orderBy, getDoc } from './firestore-compat';
import { toTitleCase } from './utils/formatters';

const TURNOS = ['Diário'];
const DIAS_SEMANA = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const ESCALAS_ENF_DESATIVADO = true;

export default function EscalasEnfermeiros() {
  const currentUser = auth.currentUser;
  
  // Date Navigation State (Starts on Today)
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Data States
  const [pacientes, setPacientes] = useState([]);
  const [escalas, setEscalas] = useState([]);
  const [profissionaisAtivos, setProfissionaisAtivos] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI States
  const [activeTab, setActiveTab] = useState('pendentes'); 
  const [viewMode, setViewMode] = useState('week'); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null); 
  const [formTecnico, setFormTecnico] = useState('');
  const [isCoringa, setIsCoringa] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userEquipe, setUserEquipe] = useState('');

  // Paciente Form States
  const [isPacienteModalOpen, setIsPacienteModalOpen] = useState(false);
  const [pacienteForm, setPacienteForm] = useState({ nome: '', dataNascimento: '', tipo: 'HB', cooperativas: [] });
  const [editingPacienteId, setEditingPacienteId] = useState(null);
  const [listaCooperativas, setListaCooperativas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch Current User Role and Equipe
  useEffect(() => {
    if (ESCALAS_ENF_DESATIVADO || !currentUser) return;
    const userEmail = currentUser.email?.toLowerCase();
    const unsubUser = onSnapshot(query(collection(db, getCollectionName('users')), where('email', '==', userEmail)), snap => {
      if (!snap.empty) {
        const data = snap.docs[0].data();
        const roles = Array.isArray(data.roles) ? data.roles : (data.role ? [data.role] : []);
        console.log("DEBUG: Sidebar/Escala user roles:", roles);
        const isAdmin = roles.includes('admin') || roles.includes('administrador') || currentUser.email === 'alexandrebareaoliveira@gmail.com';
        setUserRole(isAdmin ? 'admin' : (roles.includes('escalista') ? 'escalista' : (roles.includes('cooperativa') ? 'cooperativa' : 'leitor')));
        setUserEquipe(data.equipe || '');
      }
    }, err => {
      console.error("DEBUG: Erro ao buscar dados do usuário:", err);
    });
    return () => unsubUser();
  }, [currentUser]);

  // Fetch Pacientes e Profissionais
  useEffect(() => {
    if (ESCALAS_ENF_DESATIVADO) return;
    const unsubPacientes = onSnapshot(query(collection(db, getCollectionName('escalas_pacientes')), orderBy('nome')), snap => {
      setPacientes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => {
      console.error("DEBUG: Erro ao buscar pacientes:", err);
    });

    const unsubCooperativas = onSnapshot(query(collection(db, getCollectionName('cooperativas_config')), orderBy('name')), snap => {
      setListaCooperativas(snap.docs.map(d => d.data().name));
    }, err => {
      console.error("DEBUG: Erro ao buscar cooperativas:", err);
    });

    // Fetch Profissionais (Filtra apenas ENFERMEIROS)
    const unsubProfissionais = onSnapshot(collection(db, getCollectionName('profissionais_parceiros')), snap => {
      const profs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.statusRH !== 'Reprovado' && p.statusRH !== 'Bloqueado' && p.tipo === 'ENFERMEIRO');
      profs.sort((a, b) => a.nome.localeCompare(b.nome));
      setProfissionaisAtivos(profs);
    }, err => {
      console.error("DEBUG: Erro ao buscar profissionais:", err);
    });

    return () => { unsubPacientes(); unsubCooperativas(); unsubProfissionais(); };
  }, []);

  // Fetch Escalas — carrega tudo, filtro por dia já feito no render (dataStr === day.dateStr)
  // Evita range queries (>=, <=) no JSONB do Supabase que travam no compat layer
  useEffect(() => {
    if (ESCALAS_ENF_DESATIVADO) { setLoading(false); return; }
    const unsubscribe = onSnapshot(
      collection(db, getCollectionName('escalas_enfermeiros')),
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setEscalas(data);
        setLoading(false);
      },
      (err) => {
        console.error("DEBUG: Erro ao buscar escalas enfermeiros:", err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const changePeriod = (offset) => {
    const newDate = new Date(currentWeekStart);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (offset * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + offset);
      newDate.setDate(1);
    }
    setCurrentWeekStart(newDate);
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'week' ? 'month' : 'week';
    setViewMode(newMode);
    if (newMode === 'month') {
      const d = new Date(currentWeekStart);
      d.setDate(1);
      setCurrentWeekStart(d);
    } else {
      // Retorna para o dia atual (Hoje)
      const d = new Date();
      d.setHours(0,0,0,0);
      setCurrentWeekStart(d);
    }
  };

  const formatHeaderDate = (dateObj) => {
    const d = dateObj.getDate().toString().padStart(2, '0');
    const m = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    return `${d}/${m}`;
  };

  const displayDays = useMemo(() => {
    const days = [];
    const count = viewMode === 'week' ? 7 : new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth() + 1, 0).getDate();
    
    for (let i = 0; i < count; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push({ 
        nome: DIAS_SEMANA[date.getDay()], 
        date, 
        dateStr: date.toISOString().split('T')[0] 
      });
    }
    return days;
  }, [currentWeekStart, viewMode]);

  // Handlers
  const handleAddPaciente = () => {
    setPacienteForm({ nome: '', dataNascimento: '', tipo: 'HB', cooperativas: [] });
    setEditingPacienteId(null);
    setIsPacienteModalOpen(true);
  };

  const handleEditPaciente = (p) => {
    setPacienteForm({ 
      nome: p.nome, 
      dataNascimento: p.dataNascimento || '', 
      tipo: p.tipo || 'HB',
      cooperativas: p.cooperativas || []
    });
    setEditingPacienteId(p.id);
    setIsPacienteModalOpen(true);
  };
  const handleToggleCoopVinculo = (coop) => {
    setPacienteForm(prev => {
      const coops = prev.cooperativas || [];
      if (coops.includes(coop)) {
        return { ...prev, cooperativas: coops.filter(c => c !== coop) };
      } else {
        return { ...prev, cooperativas: [...coops, coop] };
      }
    });
  };

  const handleSavePaciente = async (e) => {
    e.preventDefault();
    if (!pacienteForm.nome) return alert("Nome é obrigatório");
    
    try {
      const formattedForm = { ...pacienteForm, nome: toTitleCase(pacienteForm.nome) };
      if (editingPacienteId) {
        await updateDoc(doc(db, getCollectionName('escalas_pacientes'), editingPacienteId), formattedForm);
      } else {
        await addDoc(collection(db, getCollectionName('escalas_pacientes')), { 
          ...formattedForm, 
          criadoEm: new Date().toISOString() 
        });
      }
      setIsPacienteModalOpen(false);
      setEditingPacienteId(null);
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar paciente.");
    }
  };

  const handleDeletePaciente = async (id) => {
    if(window.confirm('Deseja excluir este paciente da escala?')) {
      await deleteDoc(doc(db, getCollectionName('escalas_pacientes'), id));
    }
  }

  const handleOpenSlot = (paciente, day, turno) => {
    const existing = escalas.find(e => e.pacienteId === paciente.id && e.dataStr === day.dateStr && e.turno === turno);
    if (userRole === 'cooperativa') {
      const hasTecnico = existing && existing.tecnico;
      const isMyCoop = existing && existing.cooperativa === userEquipe;
      if (hasTecnico && !isMyCoop) {
        alert('Acesso Negado: Este turno já foi preenchido por outra cooperativa ou pela central.');
        return;
      }
    }
    setSelectedSlot({ paciente, day, turno, existing });
    setFormTecnico(existing ? existing.tecnico : '');
    setIsValidated(existing ? !!existing.validadoEscalista : false);
    setIsModalOpen(true);
  };

  const handleSaveSlot = async (e) => {
    e.preventDefault();
    setSaving(true);
    const profSelecionado = profissionaisAtivos.find(p => p.nome === formTecnico);
    const coringaDefinido = profSelecionado ? (profSelecionado.tipo === 'Coringa' || profSelecionado.isCoringa === true) : false;
    const cooperativaDefinida = profSelecionado ? profSelecionado.cooperativa : (userRole === 'cooperativa' ? userEquipe : '');
    
    try {
      if (userRole === 'cooperativa' && selectedSlot.existing) {
        if (selectedSlot.existing.tecnico && selectedSlot.existing.cooperativa !== userEquipe) {
          throw new Error('Acesso negado: Você não pode alterar escalas de terceiros.');
        }
      }
      if (selectedSlot.existing) {
        if (!formTecnico.trim()) {
          await deleteDoc(doc(db, getCollectionName('escalas_enfermeiros'), selectedSlot.existing.id));
        } else {
          await updateDoc(doc(db, getCollectionName('escalas_enfermeiros'), selectedSlot.existing.id), { 
            tecnico: formTecnico,
            isCoringa: coringaDefinido,
            cooperativa: cooperativaDefinida,
            validadoEscalista: isValidated
          });
        }
      } else {
        if (formTecnico.trim()) {
          await addDoc(collection(db, getCollectionName('escalas_enfermeiros')), {
            pacienteId: selectedSlot.paciente.id,
            turno: selectedSlot.turno,
            dataStr: selectedSlot.day.dateStr,
            dataObj: selectedSlot.day.date.toISOString(),
            tecnico: formTecnico,
            isCoringa: coringaDefinido,
            cooperativa: cooperativaDefinida,
            validadoEscalista: isValidated,
            criadoPor: currentUser?.email || 'unknown',
            criadoEm: new Date().toISOString()
          });
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erro ao salvar escala:", error);
      alert("Erro ao salvar escala. Verifique suas permissões.");
    } finally {
      setSaving(false);
    }
  };

  const sortedPacientes = useMemo(() => {
    const getScore = (pId) => {
      let score = 0;
        displayDays.forEach(day => {
          const hasShift = escalas.find(e => e.pacienteId === pId && e.dataStr === day.dateStr);
          if (!hasShift || !hasShift.tecnico) score += 1;
        });
      return score;
    };
    let filtered = pacientes.filter(p => p.status === 'Ativo' || !p.status);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(p => p.nome.toLowerCase().includes(q));
    }
    if (userRole === 'cooperativa' && userEquipe) {
      const myEquipe = userEquipe.trim().toUpperCase();
      filtered = filtered.filter(p => {
        const vinculadas = Array.isArray(p.cooperativas) ? p.cooperativas.map(c => c.trim().toUpperCase()) : [];
        return vinculadas.includes(myEquipe);
      });
    }
    const arr = filtered.map(p => ({ ...p, score: getScore(p.id) }));
    arr.sort((a, b) => {
      if (activeTab === 'pendentes') {
        if (b.score !== a.score) return b.score - a.score;
      }
      return a.nome.localeCompare(b.nome);
    });
    return arr;
  }, [pacientes, escalas, displayDays, activeTab, userRole, userEquipe, searchTerm]);

  const s = {
    page: { padding: '24px', fontFamily: "'Inter', sans-serif", background: '#0f172a', minHeight: '100vh', color: '#f8fafc', width: '100%', boxSizing: 'border-box' },
    header: { 
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', 
      background: 'rgba(30, 41, 59, 0.7)', padding: '20px 32px', borderRadius: '24px', 
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)', 
      border: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap', gap: '20px', backdropFilter: 'blur(10px)'
    },
    title: { margin: 0, fontSize: '1.8rem', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '12px', letterSpacing: '-0.025em' },
    legend: { 
      display: 'flex', flexWrap: 'wrap', gap: '20px', background: 'rgba(30, 41, 59, 0.5)', padding: '16px 32px', 
      borderRadius: '16px', marginBottom: '24px', fontSize: '0.8rem', fontWeight: 700, 
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' 
    },
    legendItem: () => ({ display: 'flex', alignItems: 'center', gap: '8px' }),
    legendDot: (color) => ({ width: '12px', height: '12px', borderRadius: '50%', background: color, boxShadow: `0 0 0 2px #0f172a, 0 0 0 4px ${color}66` }),
    controls: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
    btnIcon: { 
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', padding: '10px 14px', borderRadius: '12px', 
      cursor: 'pointer', fontWeight: 700, transition: 'all 0.2s', color: '#fff', 
      display: 'flex', alignItems: 'center', justifyContent: 'center' 
    },
    gridContainer: { 
      background: 'rgba(30, 41, 59, 0.4)', borderRadius: '28px', 
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', 
      padding: '24px', width: '100%', boxSizing: 'border-box', border: '1px solid rgba(255,255,255,0.1)', overflowX: 'auto' 
    },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: '8px', tableLayout: 'fixed' },
    th: { 
      background: 'rgba(15, 23, 42, 0.8)', color: '#fff', padding: '14px 10px', borderRadius: '12px', 
      textAlign: 'center', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid rgba(255,255,255,0.05)'
    },
    pacienteCell: (tipo) => ({ 
      background: tipo === 'HB' ? '#be185d' : tipo === 'HS' ? '#1d4ed8' : '#334155', 
      color: '#fff', 
      padding: '16px 12px', borderRadius: '16px', textAlign: 'center', fontWeight: 800, 
      fontSize: '0.85rem', position: 'relative', border: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
    }),
    slotCell: (status) => ({ 
      background: status === 'aberto' ? '#ff4757' : status === 'pendente' ? '#ffa502' : status === 'coringa' ? '#2e86de' : '#2ecc71', 
      color: '#fff', 
      padding: '12px 4px', borderRadius: '14px', textAlign: 'center', fontWeight: 800, 
      fontSize: '0.75rem', cursor: 'pointer', height: '56px', verticalAlign: 'middle', 
      transition: 'all 0.2s', border: 'none', width: '100%', 
      boxShadow: '0 4px 12px rgba(0,0,0,0.25)', position: 'relative', overflow: 'hidden'
    }),
    checkIcon: { position: 'absolute', top: '4px', right: '4px', fontSize: '0.6rem', background: '#fff', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { 
      background: '#fff', padding: '40px', borderRadius: '28px', width: '480px', 
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', border: '1px solid #e2e8f0' 
    },
    input: { 
      width: '100%', padding: '14px 20px', borderRadius: '14px', border: '2px solid #f1f5f9', 
      marginTop: '10px', marginBottom: '24px', outline: 'none', boxSizing: 'border-box', 
      fontSize: '1rem', transition: 'all 0.2s', background: '#f8fafc' 
    },
    btn: { 
      background: '#4f46e5', color: '#fff', border: 'none', padding: '14px 28px', 
      borderRadius: '14px', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem', 
      transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)' 
    }
  };

  if (ESCALAS_ENF_DESATIVADO) {
    return (
      <div style={{ padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
        <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Escalas de Enfermeiros desativado</h3>
        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Este módulo não está mais em uso.</p>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>🩺 Gestão de Escalas - Enfermeiros</h1>
        <div style={s.controls}>
          <button style={{...s.btn, background: '#6366f1'}} onClick={toggleViewMode}>{viewMode === 'week' ? '📅 Ver Mês' : '📅 Ver Semana'}</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button style={s.btnIcon} onClick={() => changePeriod(-1)}>◀</button>
            <div style={{ fontWeight: 800, color: '#0f172a', background: '#f8fafc', padding: '10px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', minWidth: '160px', textAlign: 'center', fontSize: '0.9rem' }}>
              {viewMode === 'week' ? `Semana: ${formatHeaderDate(displayDays[0].date)} - ${formatHeaderDate(displayDays[6].date)}` : displayDays[0].date.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase()}
            </div>
            <button style={s.btnIcon} onClick={() => changePeriod(1)}>▶</button>
          </div>
          <div style={{ position: 'relative' }}>
            <input type="text" placeholder="🔍 Buscar paciente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '12px 16px', paddingLeft: '44px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.95rem', width: '220px', outline: 'none', transition: 'all 0.2s', background: 'rgba(255,255,255,0.05)', color: '#fff' }} />
            <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', opacity: 0.6 }}>🔍</span>
          </div>
          <button style={{...s.btn, background: '#4f46e5'}} onClick={handleAddPaciente}>+ Adicionar Paciente</button>
        </div>
      </div>

      <div style={s.legend}>
        <div style={s.legendItem()}>Legendas:</div>
        <div style={s.legendItem()}><div style={s.legendDot('#ff4757')}></div> Turno em Aberto</div>
        <div style={s.legendItem()}><div style={s.legendDot('#ffa502')}></div> Enfermeiro Pendente</div>
        <div style={s.legendItem()}><div style={s.legendDot('#2ecc71')}></div> Enfermeiro Confirmado</div>
        <div style={s.legendItem()}><div style={s.legendDot('#2e86de')}></div> Profissional Coringa 🃏</div>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Carregando...</div> : (
        <div style={s.gridContainer}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{...s.th, width: '180px'}}>Paciente</th>
                {displayDays.map((day, i) => (
                  <th key={i} style={{...s.th, width: '110px'}}>
                    <div style={{fontWeight: 800}}>{viewMode === 'week' ? day.nome : day.date.getDate()}</div>
                    <div style={{fontSize:'0.65rem',opacity:0.9, marginTop: '2px'}}>{viewMode === 'week' ? formatHeaderDate(day.date) : ''}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPacientes.map(paciente => (
                <tr key={paciente.id}>
                  <td style={s.pacienteCell(paciente.tipo)}>
                    <div style={{marginBottom: '5px', fontWeight: 800}}>{paciente.nome}</div>
                    {paciente.dataNascimento && <div style={{fontSize: '0.65rem', opacity: 0.7, marginBottom: '5px'}}>🎂 {new Date(paciente.dataNascimento + 'T00:00:00').toLocaleDateString('pt-BR')}</div>}
                    <div style={{display: 'flex', gap: '4px', justifyContent: 'center'}}>
                      <button onClick={()=>handleEditPaciente(paciente)} style={{background:'rgba(0,0,0,0.05)', border:'1px solid rgba(0,0,0,0.1)', color:'#fff', cursor:'pointer', fontSize:'0.65rem', padding: '2px 6px', borderRadius: '4px', fontWeight: 600}}>Editar</button>
                      <button onClick={()=>handleDeletePaciente(paciente.id)} style={{background:'rgba(0,0,0,0.05)', border:'1px solid rgba(0,0,0,0.1)', color:'#fff', cursor:'pointer', fontSize:'0.65rem', padding: '2px 6px', borderRadius: '4px', fontWeight: 600}}>Excluir</button>
                    </div>
                  </td>
                  {displayDays.map((day, i) => {
                    const e = escalas.find(x => x.pacienteId === paciente.id && x.dataStr === day.dateStr);
                    let status = 'aberto', label = 'EM ABERTO', isOtherCoop = false;
                    if (e && e.tecnico) {
                      isOtherCoop = userRole === 'cooperativa' && e.cooperativa && e.cooperativa !== userEquipe;
                      const profInfo = profissionaisAtivos.find(p => p.nome === e.tecnico);
                      const isConfirmed = profInfo?.statusTI === 'Criado';
                      status = e.isCoringa ? 'coringa' : (isConfirmed ? 'fechado' : 'pendente');
                      if (isOtherCoop) { label = 'FECHADO'; status = 'fechado'; }
                      else { label = e.tecnico.split(' ')[0] + (e.isCoringa ? ' 🃏' : ''); }
                    }
                    return (
                      <td key={i} style={s.slotCell(status)} onClick={() => handleOpenSlot(paciente, day, 'Diário')}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ fontSize: '0.85rem' }}>{label}</div>
                          {e && e.tecnico && !isOtherCoop && <div style={{ fontSize: '0.55rem', opacity: 0.85, textTransform: 'uppercase', fontWeight: 600 }}>{e.cooperativa || 'Central'}</div>}
                        </div>
                        {e && e.validadoEscalista && <div style={s.checkIcon}>✅</div>}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div style={s.modalOverlay}>
          <form style={s.modalContent} onSubmit={handleSaveSlot}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.2rem', color: '#0f172a' }}>Escalar Enfermeiro</h2>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.9rem' }}>
              <strong>Paciente:</strong> {selectedSlot.paciente.nome}<br/>
              <strong>Data:</strong> {selectedSlot.day.nome} ({formatHeaderDate(selectedSlot.day.date)})
            </p>
            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Enfermeiro Alocado</label>
            <input style={s.input} list="profissionais-list" placeholder="Digite o nome..." value={formTecnico} onChange={e => setFormTecnico(e.target.value)} autoComplete="off" />
            <datalist id="profissionais-list">
              {profissionaisAtivos.filter(p => {
                if (userRole !== 'cooperativa') return true;
                const isOwner = p.cooperativaUid === currentUser?.uid;
                const myEquipe = (userEquipe || '').trim().toUpperCase();
                const pCoop = (p.cooperativa || '').trim().toUpperCase();
                return isOwner || (myEquipe && pCoop === myEquipe);
              }).map(p => <option key={p.id} value={p.nome}>{p.nome} {p.isCoringa ? '(Coringa)' : `(${p.tipo})`}</option>)}
            </datalist>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button type="button" style={{...s.btn, background: '#f1f5f9', color: '#475569'}} onClick={() => setIsModalOpen(false)}>Cancelar</button>
              <button type="submit" style={s.btn} disabled={saving}>{saving ? 'Salvando...' : 'Salvar Escala'}</button>
            </div>
          </form>
        </div>
      )}

      {isPacienteModalOpen && (
        <div style={s.modalOverlay}>
          <form style={s.modalContent} onSubmit={handleSavePaciente}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.2rem', color: '#0f172a' }}>
              {editingPacienteId ? 'Editar Paciente' : 'Adicionar Novo Paciente'}
            </h2>
            
            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Nome Completo</label>
            <input 
              style={s.input} 
              placeholder="Nome do paciente..."
              value={pacienteForm.nome}
              onChange={e => setPacienteForm({...pacienteForm, nome: e.target.value})}
              required
            />

            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Data de Nascimento</label>
            <input 
              type="date"
              style={s.input} 
              value={pacienteForm.dataNascimento}
              onChange={e => setPacienteForm({...pacienteForm, dataNascimento: e.target.value})}
            />

            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155', display: 'block', marginBottom: '8px' }}>Tipo de Atendimento</label>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <label style={{ 
                flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid', 
                borderColor: pacienteForm.tipo === 'HB' ? '#fce4ec' : '#e2e8f0',
                background: pacienteForm.tipo === 'HB' ? '#fce4ec' : '#fff',
                cursor: 'pointer', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem',
                color: '#000'
              }}>
                <input 
                  type="radio" name="tipo" value="HB" style={{ display: 'none' }}
                  checked={pacienteForm.tipo === 'HB'} 
                  onChange={() => setPacienteForm({...pacienteForm, tipo: 'HB'})}
                />
                Home Baby (HB)
              </label>
              <label style={{ 
                flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid', 
                borderColor: pacienteForm.tipo === 'HS' ? '#e3f2fd' : '#e2e8f0',
                background: pacienteForm.tipo === 'HS' ? '#e3f2fd' : '#fff',
                cursor: 'pointer', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem',
                color: '#000'
              }}>
                <input 
                  type="radio" name="tipo" value="HS" style={{ display: 'none' }}
                  checked={pacienteForm.tipo === 'HS'} 
                  onChange={() => setPacienteForm({...pacienteForm, tipo: 'HS'})}
                />
                Vitalcare (HS)
              </label>
            </div>

            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155', display: 'block', marginBottom: '8px' }}>Cooperativas com acesso a este paciente:</label>
            <div style={{ 
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', 
              background: '#f8fafc', padding: '12px', borderRadius: '12px', 
              border: '1px solid #e2e8f0', maxHeight: '150px', overflowY: 'auto',
              marginBottom: '24px'
            }}>
              {listaCooperativas.map(coop => (
                <label key={coop} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', cursor: 'pointer', color: '#475569', fontWeight: 600 }}>
                  <input 
                    type="checkbox" 
                    checked={(pacienteForm.cooperativas || []).includes(coop)}
                    onChange={() => handleToggleCoopVinculo(coop)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  {coop}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" style={{...s.btn, background: '#f1f5f9', color: '#475569'}} onClick={() => { setIsPacienteModalOpen(false); setEditingPacienteId(null); }}>Cancelar</button>
              <button type="submit" style={s.btn}>
                {editingPacienteId ? 'Salvar Alterações' : 'Cadastrar Paciente'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
